import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// Silence the benign "ResizeObserver loop completed with undelivered notifications"
// warning that Radix UI / Shadcn components emit. It does NOT affect functionality.
// We use TWO layers of defense so the CRA / webpack-dev-server overlay never
// pops up:
//   1) Wrap ResizeObserver so its callbacks fire inside requestAnimationFrame.
//      This prevents the loop error from being emitted in the first place.
//   2) Swallow the event at window level (capture phase) as a fallback.
// See: https://github.com/WICG/resize-observer/issues/38
// ---------------------------------------------------------------------------
const RO_MSG = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

if (typeof window !== "undefined" && window.ResizeObserver) {
  const OriginalRO = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (err) {
            if (!(err && RO_MSG.test(err.message || ""))) throw err;
          }
        });
      });
    }
  };
}

window.addEventListener(
  "error",
  (e) => {
    if (e.message && RO_MSG.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);
window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = e?.reason?.message || String(e?.reason || "");
    if (RO_MSG.test(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
